"use client";

import { ChevronDown, ChevronUp, X } from "lucide-react";
import UploadDetails from "./UploadDetails";
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useState,
} from "react";

type UploadHandler = (
  data: any,
  onProgress: (data: string) => void,
) => Promise<void>;

export type Upload = {
  id: string;
  name: string;
  type: "file" | "folder";
  status: "pending" | "processing" | "done";
  data: any;
  handler: UploadHandler;
};

interface Props {
  startUpload: (upload: Upload) => void;
}

const UploadContext = createContext<Props>({
  startUpload: () => {},
});

export default function UploadProvider({ children }: { children?: ReactNode }) {
  const [uploads, setUploads] = useState<Upload[]>([]);
  const [collapse, setCollapse] = useState(false);
  const [open, setOpen] = useState(false);

  const startUpload = useCallback((upload: Upload) => {
    setUploads((state) => [upload, ...state]);
    setCollapse(false);
    setOpen(true);
  }, []);

  const onCollapse = () => setCollapse(!collapse);

  const onClose = () => {
    setOpen(false);
    setUploads((pre) => pre.filter((u) => u.status !== "done"));
  };

  return (
    <UploadContext.Provider value={{ startUpload }}>
      <div
        className={`fixed right-4 bottom-4 z-999 w-[300px] flex-col overflow-hidden rounded border border-gray-300 bg-white ${open ? "flex" : "hidden"} `}
      >
        <div className="text-md flex w-full items-center justify-start bg-gray-100 px-2 py-2">
          <span className="font-bold">{`${uploads.length} upload${uploads.length > 1 ? "s" : ""}`}</span>
          <span className="ml-auto cursor-pointer" onClick={onCollapse}>
            {collapse ? <ChevronUp size={24} /> : <ChevronDown size={24} />}
          </span>
          <span className="ml-2 cursor-pointer" onClick={onClose}>
            <X size={24} />
          </span>
        </div>
        <div
          className="flex w-full flex-col overflow-auto"
          style={{
            maxHeight: collapse ? 0 : 200,
          }}
        >
          {uploads.map((upload) => (
            <UploadDetails key={upload.id} upload={upload} />
          ))}
        </div>
      </div>
      {children}
    </UploadContext.Provider>
  );
}

export const useUpload = () => useContext(UploadContext);
